import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTenantConfig, tenantLogoUrl } from '../../lib/api';
import { hostLogoOverride } from '../../lib/host-logo';
import { useThemeMode } from '../../lib/theme-mode';
import { useBrand } from '../lib/hooks';

// Build-time baked brand (vite `define`) — used as the initial value so the
// logo renders the tenant's mark on the FIRST paint, before the runtime
// `['tenant-config']` query resolves. Empty string on an un-themed build.
const BUILD_LOGO = __TENANT_LOGO__ || null;

/**
 * Resolve the tenant's logo image URL + text wordmark from the shared
 * `['tenant-config']` query. An uploaded logo key (served via `/tenant/logo`)
 * wins over a pasted `logoUrl`, which wins over the build-time baked logo. The
 * key is cache-busted so a re-upload to the same slot refreshes.
 *
 * In dark mode the dark-surface logo (uploaded key, then URL) is tried first,
 * falling back to the light one — the light mark is typically black ink, which
 * vanishes on a dark header.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the logo resolver is shared by the header marks here and the portal sidebar
export function useBrandLogo() {
  const { data } = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig, retry: 0 });
  const brand = useBrand();
  const theme = data?.theme;
  const mode = useThemeMode((s) => s.mode);
  // Same gate as theme-boot: no dark token set, no dark mode.
  const dark = mode === 'dark' && !!theme?.dark;
  const darkLogo = !dark
    ? null
    : theme?.logoKeyDark
      ? `${tenantLogoUrl('dark')}?v=${encodeURIComponent(theme.logoKeyDark)}`
      : (theme?.logoUrlDark ?? null);
  const computed =
    darkLogo ??
    (theme?.logoKey
      ? `${tenantLogoUrl('light')}?v=${encodeURIComponent(theme.logoKey)}`
      : (theme?.logoUrl ?? BUILD_LOGO));
  // A per-hostname override (multi-domain tenants) wins over every source above,
  // including an uploaded logo, so a domain always shows its own mark. Resolved
  // synchronously from window.location + a build constant → correct on first paint.
  const logoUrl = hostLogoOverride() ?? computed;
  const wordmark = theme?.wordmark ?? (__TENANT_WORDMARK__ || null);
  return { logoUrl, wordmark, brand };
}

/**
 * House-store avatar / mark. The tenant's uploaded/URL logo when set, else a
 * neutral brand-initial avatar on the tenant's primary color — no hardcoded
 * showroom mark. Data-driven from the shared `['tenant-config']` query so every
 * house badge follows the tenant brand.
 */
export function OmniMarkOrange({ size = 38 }: { size?: number }) {
  const { logoUrl, brand } = useBrandLogo();
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={brand.brandName}
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
      />
    );
  }
  const initial = brand.brandName.trim().slice(0, 1).toUpperCase() || '•';
  return (
    <div
      role="img"
      aria-label={brand.brandName}
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.28),
        background: 'hsl(var(--primary))',
        color: 'hsl(var(--primary-foreground))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: size * 0.5,
        lineHeight: 1,
      }}
    >
      {initial}
    </div>
  );
}

/**
 * Header wordmark — data-driven from `tenant_config.theme`. Resolution order:
 *   1. uploaded logo / `theme.logoUrl` -> the tenant's logo <img>
 *   2. `theme.wordmark`                 -> the tenant's text wordmark
 *   3. neither                          -> brand-initial mark + the brand name
 * The build-time `__TENANT_LOGO__`/`__TENANT_WORDMARK__` seed the first paint so
 * a themed build never flashes before the query resolves. The wordmark text uses
 * `text-o` (tenant-primary), so it follows the brand color.
 */
export function OmniWordmark({ size = 38, sub }: { size?: number; sub?: string }) {
  const { logoUrl, wordmark, brand } = useBrandLogo();

  let mark: ReactNode;
  if (logoUrl) {
    mark = (
      <img
        src={logoUrl}
        alt={wordmark ?? brand.brandName}
        style={{ height: size, width: 'auto', display: 'block' }}
      />
    );
  } else if (wordmark) {
    mark = (
      <span
        className="font-extrabold tracking-tight text-o"
        style={{ fontSize: size * 0.79, letterSpacing: '-0.03em' }}
      >
        {wordmark}
      </span>
    );
  } else {
    // Neutral fallback: brand-initial mark + the resolved brand name.
    mark = (
      <>
        <OmniMarkOrange size={size} />
        <span
          className="font-extrabold tracking-tight text-o"
          style={{ fontSize: size * 0.79, letterSpacing: '-0.03em' }}
        >
          {brand.brandName}
        </span>
      </>
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      {mark}
      {sub && (
        <span className="font-semibold text-o" style={{ fontSize: size * 0.55 }}>
          {sub}
        </span>
      )}
    </span>
  );
}
